import AppContext from '@/AppContext';
import { EntityList } from '@/components/EntityList';
import { ListColumn } from '@/components/ListLayout';
import { getFontAwesomeIconForFileExtension } from '@/lib/utils';
import { Form, Grid, Label, Pagination } from '@/semantic-ui/components';
import { useStores } from '@/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useCallback, useContext, useState } from 'react';
import { useSearchParams } from 'react-router';
import { OnboardingForm, toNum } from 'sol-data';
import PositionFilterDropdown from '../PositionFilterDropdown';

const RESULTS_PER_PAGE = 10;
const DEBOUNCE_DELAY = 200;

const deleteWarning = 'Are you sure you want to delete this Onboarding Form?';

export const OnboardingFormTemplatesList = observer(() => {
  const { onboardingFormStore } = useStores();
  const [onboardingForms, setOnboardingForms] = useState<OnboardingForm[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const appContext = useContext(AppContext);
  const isAdmin = appContext?.isAdmin;
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get('page') ? Number(searchParams.get('page')) : 1;
  const positionId = searchParams.get('position')
    ? Number(searchParams.get('position'))
    : undefined;
  const [search, setSearch] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const fetchData = async (
    pageNumber: number,
    searchValue?: string,
    positionIdValue?: number,
    includeDeletedValue?: boolean,
  ) => {
    onboardingFormStore.clearCurrent();

    const data = await onboardingFormStore.fetchAllPaginated({
      page: pageNumber,
      search: searchValue,
      positionId: positionIdValue,
      includeDeleted: includeDeletedValue ?? includeDeleted,
    });

    setCurrentPage(pageNumber);
    setOnboardingForms(data);
  };

  const handlePositionChange = async (value: number) => {
    setSearch('');

    await changePage(1, '', value, includeDeleted);
  };

  const changePage = async (
    pageNumber: number,
    searchValue?: string,
    positionIdValue?: number,
    includeDeletedValue?: boolean,
  ) => {
    onboardingFormStore.setPage(pageNumber);

    setSearchParams({
      page: pageNumber.toString(),
      position: positionIdValue?.toString() ?? '',
    });

    await fetchData(pageNumber, searchValue, positionIdValue, includeDeletedValue);
  };

  const updateSearch = useCallback(
    debounce(async (searchValue: string) => {
      await changePage(1, searchValue, positionId, includeDeleted);
    }, DEBOUNCE_DELAY),
    [page, positionId, includeDeleted],
  );

  const handleIncludeDeletedChange = async (checked: boolean) => {
    setIncludeDeleted(checked);
    await changePage(page, search, positionId, checked);
  };

  const { isLoading, errorMessage } = onboardingFormStore;

  const columns: ListColumn[] = [
    {
      name: 'Title',
      key: 'title',
      width: { sm: 3, lg: 3 },
      render: (item: any) => (
        <span
          style={{
            opacity: item.deleteDatetime ? 0.6 : 1,
            textDecoration: item.deleteDatetime ? 'line-through' : 'none',
            color: item.deleteDatetime ? '#999' : 'inherit',
          }}
        >
          {item.title}
          {item.deleteDatetime && (
            <Label size="mini" color="grey" style={{ marginLeft: 8 }}>
              ARCHIVED
            </Label>
          )}
        </span>
      ),
    },
    {
      name: 'Created By',
      key: 'postedBy',
      width: { sm: 2, lg: 2 },
      textAlign: 'center',
      render: ({ postedBy }) => (
        <div
          style={{
            textAlign: 'center',
          }}
        >
          {postedBy}
        </div>
      ),
    },
    {
      name: 'Posted On',
      key: 'postedOn',
      width: { sm: 2, lg: 2 },
      textAlign: 'center',
      render: ({ postedOn }) => (
        <div
          style={{
            textAlign: 'center',
          }}
        >
          {postedOn}
        </div>
      ),
    },
    {
      name: 'Positions',
      key: 'positions',
      width: { sm: 3, lg: 3 },
      textAlign: 'center',
      render: ({ positions }) => (
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '4px',
          }}
        >
          {positions.map(({ label, id }: { label: string; id: number }) => (
            <Label key={id} size="tiny" color="blue">
              {label.toUpperCase()}
            </Label>
          ))}
        </div>
      ),
    },
    {
      name: 'Require Signature',
      key: 'requireSignature',
      width: { sm: 1, lg: 1 },
      textAlign: 'center',
      render: ({ requireSignature }) => (
        <div style={{ textAlign: 'center' }}>{requireSignature ? 'Yes' : 'No'}</div>
      ),
    },
    {
      name: 'Require User Upload',
      key: 'requireUserUpload',
      width: { sm: 1, lg: 1 },
      textAlign: 'center',
      render: ({ requireUserUpload }) => (
        <div style={{ textAlign: 'center' }}>{requireUserUpload ? 'Yes' : 'No'}</div>
      ),
    },
    {
      name: 'Example File Name',
      key: 'fileName',
      width: { sm: 3, lg: 3 },
      render: ({ exampleFileName }) =>
        exampleFileName ? (
          <div
            style={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            <FontAwesomeIcon
              icon={['far', getFontAwesomeIconForFileExtension(exampleFileName.split('.').pop()!)]}
              className="reveal-icon"
              style={{ marginRight: 4 }}
            />
            {exampleFileName}
          </div>
        ) : (
          <>
            <FontAwesomeIcon
              icon={['far', 'file-times']}
              className="reveal-icon"
              style={{ marginRight: 4 }}
            />
            N/A
          </>
        ),
    },
  ];

  const handleRemove = async (id: number) => {
    await onboardingFormStore.delete(id);
    setOnboardingForms(onboardingForms.filter((upload) => upload.id !== id));
  };

  return (
    <>
      <EntityList
        title="Onboarding Forms"
        searchLabel="Search by document or file name"
        columns={columns}
        items={onboardingForms}
        newLink="/onboarding/forms/templates/add"
        titleProp="title"
        onRemove={({ id }) => handleRemove(id)}
        dialog={deleteWarning}
        searchValue={search}
        onSearchChange={(filter) => {
          setSearch(filter);
          updateSearch(filter);
        }}
        onDismiss={() => onboardingFormStore.clearError()}
        onRefresh={() => changePage(page, search, positionId, includeDeleted)}
        errorMessage={errorMessage}
        isLoading={isLoading}
        link={
          isAdmin
            ? ({ id, deleteDatetime }) =>
                deleteDatetime ? '#' : `/onboarding/forms/templates/${id}`
            : undefined
        }
        noNewButton={!isAdmin}
        searchHeaderContent={
          <Form
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 10,
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Form.Field style={{ margin: 0 }}>
              <Form.Checkbox
                label="Show archived templates"
                checked={includeDeleted}
                onChange={(_, { checked }) => handleIncludeDeletedChange(checked || false)}
              />
            </Form.Field>
            <Form.Field>
              <PositionFilterDropdown
                limited
                clearable
                name="position"
                placeholder="Filter by position"
                positionId={positionId ? toNum(positionId) : undefined}
                onChange={(_, value) => handlePositionChange(value)}
              />
            </Form.Field>
          </Form>
        }
      />
      <Grid centered>
        <Grid.Row>
          <Pagination
            activePage={currentPage}
            totalPages={Math.ceil(onboardingFormStore.allUploadsCount / RESULTS_PER_PAGE)}
            onPageChange={(_, { activePage }) => {
              changePage(activePage as number, search, positionId, includeDeleted);
            }}
            siblingRange={2}
            boundaryRange={0}
            firstItem={{ content: 'First' }}
            lastItem={{ content: 'Last' }}
            ellipsisItem={null}
            prevItem={null}
            nextItem={null}
          />
        </Grid.Row>
      </Grid>
    </>
  );
});
